/**
 * Booking Intake Hub: landing page with three tabs:
 *   1. Quick Notes  : type-it-in, then convert to a draft
 *   2. Paste Chat   : paste from Messenger/WhatsApp/SMS/Viber, then convert
 *   3. Drafts       : list of pending drafts grouped by status
 *
 * Conversion goes through the hybrid extractor on the backend. Staff still
 * has to review and approve every draft before the customer is messaged.
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import {
  Inbox, FileText, MessageSquareText, ClipboardList,
  AlertTriangle, ShieldCheck, Loader2, ArrowRight, FileSpreadsheet,
  Smartphone,
} from "lucide-react";
import CsvImportPanel from "@/components/booking_intake/CsvImportPanel";
import useInstallPrompt from "@/hooks/useInstallPrompt";

const SOURCE_OPTIONS = [
  ["messenger", "Messenger"],
  ["whatsapp", "WhatsApp"],
  ["viber", "Viber"],
  ["sms", "SMS"],
  ["facebook", "Facebook"],
  ["phone", "Phone call"],
  ["walk_in", "Walk-in"],
  ["quick_note", "Quick note"],
  ["other", "Other"],
];

const STATUS_LABEL = {
  draft_extracted: "Extracted",
  staff_review_required: "Needs review",
  staff_approved: "Approved",
  confirmation_sent: "Awaiting customer",
  customer_confirmed: "Customer confirmed",
  customer_requested_change: "Change requested",
  discarded: "Discarded",
};

const STATUS_TONE = {
  staff_review_required: "bg-amber-100 text-amber-900 border-amber-200",
  staff_approved: "bg-sky-100 text-sky-900 border-sky-200",
  confirmation_sent: "bg-indigo-100 text-indigo-900 border-indigo-200",
  customer_confirmed: "bg-emerald-100 text-emerald-900 border-emerald-200",
  customer_requested_change: "bg-rose-100 text-rose-900 border-rose-200",
  discarded: "bg-slate-100 text-slate-600 border-slate-200",
};

export default function BookingIntake() {
  const [tab, setTab] = useState("quick_note");
  const [drafts, setDrafts] = useState([]);
  const [loadingDrafts, setLoadingDrafts] = useState(true);

  const loadDrafts = async () => {
    setLoadingDrafts(true);
    try {
      const { data } = await api.get("/booking-intake/drafts");
      setDrafts(data.items || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoadingDrafts(false);
    }
  };
  useEffect(() => { loadDrafts(); }, []);

  const grouped = useMemo(() => {
    const buckets = { needs_review: [], in_flight: [], done: [] };
    for (const d of drafts) {
      if (d.status === "staff_review_required") buckets.needs_review.push(d);
      else if (["staff_approved", "confirmation_sent",
                 "customer_requested_change"].includes(d.status)) {
        buckets.in_flight.push(d);
      } else buckets.done.push(d);
    }
    return buckets;
  }, [drafts]);

  return (
    <div className="space-y-6" data-testid="booking-intake-page">
      <header>
        <h1 className="font-display text-3xl sm:text-4xl font-bold">
          Booking Intake Hub
        </h1>
        <p className="text-slate-600 mt-1 max-w-2xl">
          Turn messy Messenger / WhatsApp / Viber / SMS / walk-in notes into
          proper ConfirmPH booking records. Every draft needs staff review
          before the customer ever sees it.
        </p>
        <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-sm">
          <Link to="/app/booking-intake/channels"
                 data-testid="open-intake-channels"
                 className="text-[#1B4332] font-medium hover:underline">
            Manage official channels →
          </Link>
          <Link to="/notepad"
                 data-testid="open-staff-notepad"
                 className="text-[#1B4332] font-medium hover:underline">
            Open staff notepad (offline-friendly) →
          </Link>
        </div>
      </header>

      <InstallNotepadShortcut />


      <nav className="inline-flex rounded-xl border border-slate-200 bg-white p-1 text-sm font-medium"
            data-testid="booking-intake-tabs">
        <TabBtn id="quick_note" active={tab} setActive={setTab}
                 Icon={FileText} label="Quick Note" />
        <TabBtn id="paste_chat" active={tab} setActive={setTab}
                 Icon={MessageSquareText} label="Paste Chat" />
        <TabBtn id="csv" active={tab} setActive={setTab}
                 Icon={FileSpreadsheet} label="CSV / Sheets" />
        <TabBtn id="drafts" active={tab} setActive={setTab}
                 Icon={ClipboardList}
                 label={`Drafts${drafts.length ? ` · ${drafts.length}` : ""}`} />
      </nav>

      {tab === "quick_note" && (
        <QuickNoteForm onCreated={loadDrafts} />
      )}
      {tab === "paste_chat" && (
        <PasteChatForm onCreated={loadDrafts} />
      )}
      {tab === "csv" && (
        <CsvImportPanel onCommitted={loadDrafts} />
      )}
      {tab === "drafts" && (
        <DraftLists loading={loadingDrafts} grouped={grouped} />
      )}
    </div>
  );
}

function TabBtn({ id, active, setActive, Icon, label }) {
  const on = active === id;
  return (
    <button
      type="button"
      onClick={() => setActive(id)}
      data-testid={`booking-intake-tab-${id}`}
      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg transition ${
        on ? "bg-[#1B4332] text-white" : "text-slate-700 hover:bg-slate-100"
      }`}
    >
      <Icon className="w-4 h-4" /> {label}
    </button>
  );
}

function QuickNoteForm({ onCreated }) {
  const navigate = useNavigate();
  const [text, setText] = useState("");
  const [source, setSource] = useState("quick_note");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!text.trim()) { toast.error("Type a note first"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/booking-intake/quick-notes", {
        text, source_channel: source, convert_now: true,
      });
      toast.success("Draft created: review it next.");
      onCreated?.();
      if (data.draft?.id) navigate(`/app/booking-intake/drafts/${data.draft.id}`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-6 space-y-4"
          data-testid="quick-note-form">
      <div className="flex items-start gap-3">
        <Inbox className="w-5 h-5 text-emerald-700 flex-shrink-0 mt-1" />
        <div>
          <h2 className="font-display text-lg font-semibold">Quick Note</h2>
          <p className="text-xs text-slate-500 mt-0.5">
            Write the booking the way you'd normally write it on paper. We'll
            extract the details into a reviewable draft.
          </p>
        </div>
      </div>

      <label className="block">
        <span className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
          Note
        </span>
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          data-testid="quick-note-text"
          placeholder="e.g. John Santos, 09171234567, Toyota Vios AT, July 14 8am to July 16 5pm, pickup Dumaguete airport, 2500/day, deposit 1000 paid GCash, return same place."
          className="mt-1 w-full min-h-[160px] rounded-lg border border-slate-300 focus:border-[#2D6A4F] outline-none text-sm p-3"
        />
      </label>
      <SourceSelect value={source} onChange={setSource} testId="quick-note-source" />
      <div className="flex justify-end gap-2">
        <button
          type="button"
          onClick={submit}
          disabled={busy}
          data-testid="quick-note-convert"
          className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-lg btn-brand font-medium disabled:opacity-60"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" />
                : <ArrowRight className="w-4 h-4" />}
          Convert to Booking Draft
        </button>
      </div>
    </div>
  );
}

function PasteChatForm({ onCreated }) {
  const navigate = useNavigate();
  const [text, setText] = useState("");
  const [source, setSource] = useState("messenger");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!text.trim()) { toast.error("Paste the chat first"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/booking-intake/paste-chat", {
        text, source_channel: source,
      });
      toast.success("Draft created: review it next.");
      onCreated?.();
      if (data?.id) navigate(`/app/booking-intake/drafts/${data.id}`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-6 space-y-4"
          data-testid="paste-chat-form">
      <div className="flex items-start gap-3">
        <MessageSquareText className="w-5 h-5 text-emerald-700 flex-shrink-0 mt-1" />
        <div>
          <h2 className="font-display text-lg font-semibold">Paste Chat</h2>
          <p className="text-xs text-slate-500 mt-0.5">
            Paste a Messenger / WhatsApp / Viber / SMS / Facebook conversation
            below. ConfirmPH will extract likely booking details. Nothing is
            confirmed until staff reviews and the customer accepts the link.
          </p>
        </div>
      </div>

      <label className="block">
        <span className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
          Pasted chat
        </span>
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          data-testid="paste-chat-text"
          placeholder="Paste the entire conversation here…"
          className="mt-1 w-full min-h-[260px] rounded-lg border border-slate-300 focus:border-[#2D6A4F] outline-none text-sm p-3"
        />
      </label>
      <SourceSelect value={source} onChange={setSource} testId="paste-chat-source" />
      <div className="flex justify-end gap-2">
        <button
          type="button"
          onClick={submit}
          disabled={busy}
          data-testid="paste-chat-convert"
          className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-lg btn-brand font-medium disabled:opacity-60"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" />
                : <ArrowRight className="w-4 h-4" />}
          Paste chat and convert
        </button>
      </div>
    </div>
  );
}

function SourceSelect({ value, onChange, testId }) {
  return (
    <label className="block max-w-xs">
      <span className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
        Where did this come from?
      </span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testId}
        className="mt-1 w-full h-10 rounded-lg border border-slate-300 bg-white px-3 text-sm"
      >
        {SOURCE_OPTIONS.map(([v, label]) => (
          <option key={v} value={v}>{label}</option>
        ))}
      </select>
    </label>
  );
}
function InstallNotepadShortcut() {
  const { installed } = useInstallPrompt();
  return (
    <section className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 flex items-start gap-3"
              data-testid="install-staff-notes">
      <Smartphone className="w-5 h-5 text-emerald-700 flex-shrink-0 mt-1" />
      <div className="flex-1">
        <h3 className="font-display font-semibold text-emerald-900"
            data-testid="install-staff-notes-title">
          {installed ? "Open Staff Notes" : "Install Staff Notes"}
        </h3>
        <p className="text-xs text-emerald-900/80 mt-0.5 max-w-xl">
          Pin a focused notepad on your phone home screen. Staff can type
          or paste booking details, even when offline: then sync and
          convert them into ConfirmPH booking drafts.
        </p>
        <div className="mt-2 flex flex-wrap gap-2">
          <Link to="/notepad" data-testid="install-staff-notes-go-btn"
                 className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg btn-brand text-sm font-medium">
            {installed ? "Open Staff Notes" : "Open Staff Notes to install"} <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>
      </div>
    </section>
  );
}




function DraftLists({ loading, grouped }) {
  if (loading) return (
    <div className="rounded-xl border border-slate-200 bg-white p-6 text-slate-500 flex items-center gap-2">
      <Loader2 className="w-4 h-4 animate-spin" /> Loading drafts…
    </div>
  );
  const sections = [
    ["needs_review", "Needs review", grouped.needs_review],
    ["in_flight", "Awaiting customer / change requested", grouped.in_flight],
    ["done", "Customer confirmed / Discarded", grouped.done],
  ];
  return (
    <div className="space-y-6">
      {sections.map(([key, title, items]) => (
        <section key={key} data-testid={`drafts-section-${key}`}>
          <h2 className="font-display text-lg font-semibold text-slate-900 mb-2">
            {title}{" "}
            <span className="text-xs font-normal text-slate-500">
              {items.length}
            </span>
          </h2>
          {items.length === 0 ? (
            <p className="text-sm text-slate-500">Nothing here yet.</p>
          ) : (
            <ul className="space-y-2">
              {items.map((d) => <DraftRow key={d.id} d={d} />)}
            </ul>
          )}
        </section>
      ))}
    </div>
  );
}

function DraftRow({ d }) {
  const ex = d.extracted || {};
  return (
    <li className="rounded-xl border border-slate-200 bg-white p-4 hover:border-emerald-600 transition"
        data-testid={`draft-row-${d.id}`}>
      <Link to={`/app/booking-intake/drafts/${d.id}`}
             className="block">
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <span className="font-semibold text-slate-900">
            {ex.customer_name || <em className="text-slate-400">No customer name</em>}
          </span>
          <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded border ${STATUS_TONE[d.status] || "bg-slate-100 text-slate-700 border-slate-200"}`}>
            {STATUS_LABEL[d.status] || d.status}
          </span>
          <span className="text-xs text-slate-500">
            {d.source_channel?.replace("_", " ")}
          </span>
        </div>
        <div className="text-xs text-slate-600 mt-1">
          {d.matched_vehicle?.name || ex.vehicle_text || "No vehicle"} ·{" "}
          {ex.start_datetime?.slice(0, 16).replace("T", " ") || "-"} →{" "}
          {ex.end_datetime?.slice(0, 16).replace("T", " ") || "-"}
        </div>
        {(d.warnings?.length || 0) > 0 && (
          <div className="mt-2 inline-flex items-center gap-1 text-xs text-amber-800">
            <AlertTriangle className="w-3 h-3" /> {d.warnings.length} warning{d.warnings.length === 1 ? "" : "s"}
          </div>
        )}
        {d.status === "customer_confirmed" && (
          <div className="mt-2 inline-flex items-center gap-1 text-xs text-emerald-700">
            <ShieldCheck className="w-3 h-3" /> Customer confirmed
          </div>
        )}
      </Link>
    </li>
  );
}
